import { useMemo, useState } from 'react'
import { Text, View } from 'react-native'
import { twMerge } from 'tailwind-merge'
import { z } from 'zod'

import { colorEnum, horizontalAlignmentEnum } from '@/cms/helperTypes'
import type { HorizontalAlignment } from '@/cms/helperTypes'
import { Icon } from '@/components/Icon'
import { MAX_MODAL_WIDTH, Modal } from '@/components/Modal'
import { reportWarning } from '@/services/errorReporting'
import type { PortableTextTypeComponentProps } from '@portabletext/react'
import ButtonIcon from '@ui-kit/components/ButtonIcon'
import { body1, body2, caption, h1, h2, h3, h4, h5 } from '@ui-kit/core/typography'
import type { Color } from '@ui-kit/types'

import { BlockContent } from './BlockContent'

export const configurableTypography = z.object({
  _type: z.literal('configurableTypography'),
  type: z.enum(['h1', 'h2', 'h3', 'h4', 'h5', 'normal', 'body2', 'caption']),
  bold: z.boolean().nullable().optional(),
  font: z.enum(['primary', 'secondary']).nullable().optional(),
  color: colorEnum.nullable().optional(),
  emphasis: z.enum(['default', 'medium', 'low']).nullable().optional(),
  textAlign: horizontalAlignmentEnum.nullable().optional(),
  underlined: z.boolean().nullable().optional(),
  content: z.string(),
  infoModal: z
    .object({
      id: z.string(),
      content: z.array(z.unknown()),
    })
    .optional(),
})

type ConfigurableTypography = z.infer<typeof configurableTypography>

const classFunctions = {
  /*
      This is a workaround to fix the issue
      https://hellogetsafe.atlassian.net/browse/BX-668
    */
  h1,
  h2,
  h3,
  h4,
  h5,
  normal: body1,
  body2: body2,
  caption: caption,
}

// Accessibility Props is used to set the role of the Text component
// https://necolas.github.io/react-native-web/docs/accessibility
const accessibilityProps = {
  h1: {
    role: 'heading',
  },
  h2: {
    role: 'heading',
    'aria-level': 2,
  },
  h3: {
    role: 'heading',
    'aria-level': 3,
  },
  h4: {
    role: 'heading',
    'aria-level': 4,
  },
  h5: {
    role: 'heading',
    'aria-level': 5,
  },
  normal: {
    role: 'paragraph',
  },
  body2: {
    role: 'paragraph',
  },
  caption: {
    // NOTE: this is wrong, caption must be a child of a 'figure' element
    // role: 'caption',
    role: 'paragraph',
  },
} as const

export const ConfigurableTypography = (props: {
  node: PortableTextTypeComponentProps<unknown>
  align: HorizontalAlignment
  pageColor?: Color
}) => {
  const value = useMemo(() => configurableTypography.parse(props.node.value), [props.node.value])
  const { type, bold, font, color, emphasis, underlined, content, textAlign } = value
  const [isModalVisible, setIsModalVisible] = useState(false)
  if (!content) {
    return null
  }

  const classNameFunction = classFunctions[type]

  if (!classNameFunction) {
    reportWarning(`Type "${type}" doesn't have a renderer`)
    return null
  }

  // Replace \n with <br />
  const preparedContent = content
    .split('\n')
    // @ts-expect-error
    .reduce((prev, curr, index) => [...prev, <br key={`br-${curr.toString()}-${index}`} />, curr])

  return (
    <>
      <View className="flex w-full flex-row items-center">
        {/* https://necolas.github.io/react-native-web/docs/accessibility/ */}
        {/* @ts-expect-error role paragraph is valid role */}
        <Text
          {...accessibilityProps[type]}
          className={twMerge(
            'w-full',
            classNameFunction({
              type: font ?? undefined,
              color: props.pageColor ?? color ?? undefined,
              emphasis: emphasis ?? undefined,
              bold: bold ?? undefined,
              align: textAlign ?? props.align,
            }),
            underlined && 'underline'
          )}
        >
          {preparedContent}
        </Text>
        {value.infoModal ? (
          <ButtonIcon
            onPress={() => setIsModalVisible(true)}
            icon={
              <Icon
                size="small"
                icon={{
                  name: 'circleInfo_24',
                }}
              />
            }
          />
        ) : null}
      </View>
      {value.infoModal ? (
        <Modal visible={isModalVisible} onClose={() => setIsModalVisible(false)}>
          <BlockContent
            blocks={value.infoModal.content}
            width={MAX_MODAL_WIDTH}
            align={props.align}
            sectionName="infoModal"
            space="none"
          />
        </Modal>
      ) : null}
    </>
  )
}
